/**
 * Tracks the previous in-app pathname so back-navigation can avoid landing
 * on routes that would create a loop. Next.js' App Router uses SPA
 * navigation, which means `document.referrer` is frozen at the document's
 * initial load and does NOT reflect subsequent client-side navigations;
 * relying on it for "where did the user just come from?" gives the wrong
 * answer after the first hop. We instead listen for pathname changes from
 * a layout-level hook and remember the last value.
 */

const STORAGE_KEY = 'nav-history-v1';

let currentPathname: string | null = null;
let previousPathname: string | null = null;
let loaded = false;

function load(): void {
  if (typeof window === 'undefined' || loaded) return;
  loaded = true;
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw) as { current?: string; previous?: string };
    currentPathname = parsed.current ?? null;
    previousPathname = parsed.previous ?? null;
  } catch {
    // ignore: sessionStorage unavailable or corrupted
  }
}

function save(): void {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ current: currentPathname, previous: previousPathname }),
    );
  } catch {
    // ignore
  }
}

// Back and Forward. A popstate marks the navigation now rendering as a history
// traversal, and the next recorded pathname (ClientLayout records each one
// after its navigation commits) closes it. A page's own effects run before
// ClientLayout's, so a page mounting from Back reads true. The marketplace
// uses it to put a shopper back where they were when the browser could not
// (scrollToRestore in src/lib/marketplace-reveal.ts).
let traversal = false;
if (typeof window !== 'undefined') {
  window.addEventListener('popstate', () => {
    traversal = true;
  });
}

/** Whether the navigation now rendering came from Back or Forward. */
export function isHistoryTraversal(): boolean {
  return traversal;
}

export function recordPathname(pathname: string): void {
  traversal = false;
  load();
  if (currentPathname === pathname) return;
  previousPathname = currentPathname;
  currentPathname = pathname;
  save();
}

export function getPreviousPathname(): string | null {
  load();
  return previousPathname;
}
